import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sidebar } from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Plus, Lightbulb, Search, Sparkles, Bookmark, BookmarkCheck, Instagram, Youtube } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  'Beauty',
  'Fashion',
  'Fitness',
  'Food',
  'Travel',
  'Technology',
  'Lifestyle',
  'Tutorial',
  'Review',
  'Vlog',
  'Behind-the-scenes',
  'Challenge',
  'Educational',
  'Entertainment'
];

export const ContentIdeas = () => {
  const [savedIdeas, setSavedIdeas] = useState([]);
  const [generatedIdeas, setGeneratedIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [savedIds, setSavedIds] = useState(new Set());
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Beauty'
  });

  useEffect(() => {
    fetchSavedIdeas();
  }, []);

  const fetchSavedIdeas = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/content-ideas`, { withCredentials: true });
      setSavedIdeas(data);
    } catch (error) {
      console.error('Failed to fetch content ideas:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateIdeas = async (category, prompt) => {
    setGenerating(true);
    setGeneratedIdeas([]);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/content-ideas/generate`,
        { category: category || null, prompt: prompt || null, count: 8 },
        { withCredentials: true }
      );
      setGeneratedIdeas(data);
      setSavedIds(new Set());
    } catch (error) {
      console.error('Failed to generate ideas:', error);
      alert(error.response?.data?.detail || 'Failed to generate ideas');
    } finally {
      setGenerating(false);
    }
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    if (category && category !== 'all') {
      generateIdeas(category, '');
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      generateIdeas(selectedCategory === 'all' ? '' : selectedCategory, searchQuery.trim());
    }
  };

  const openLink = (url, platform) => {
    if (!url) return;

    // Try opening in new tab (works outside iframe)
    let opened = null;
    try {
      opened = window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      opened = null;
    }

    // If popup was blocked or returned null, try top-level
    if (!opened) {
      try {
        opened = window.top.open(url, '_blank', 'noopener,noreferrer');
      } catch (e) {
        opened = null;
      }
    }

    if (opened) {
      toast.success(`Opening ${platform}...`);
      return;
    }

    // Iframe is blocking popups - copy URL to clipboard
    const fallback = () => {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = url;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        return true;
      } catch (e) {
        return false;
      }
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url)
        .then(() => toast.success(`${platform} link copied! Paste in a new browser tab.`, { duration: 5000 }))
        .catch(() => {
          if (fallback()) {
            toast.success(`${platform} link copied! Paste in a new browser tab.`, { duration: 5000 });
          } else {
            toast.error('Could not open link. Right-click the button → "Copy link address"');
          }
        });
    } else if (fallback()) {
      toast.success(`${platform} link copied! Paste in a new browser tab.`, { duration: 5000 });
    } else {
      toast.error('Could not open link. Try opening the app in a new tab.');
    }
  };

  const saveGeneratedIdea = async (idea, index) => {
    try {
      const hashtagsText = idea.hashtags?.length ? `\n\nHashtags: ${idea.hashtags.map(h => '#' + h).join(' ')}` : '';
      const refsText = (idea.instagram_url || idea.youtube_url)
        ? `\n\nReferences:${idea.instagram_url ? '\nInstagram: ' + idea.instagram_url : ''}${idea.youtube_url ? '\nYouTube: ' + idea.youtube_url : ''}`
        : '';
      await axios.post(
        `${API_URL}/api/content-ideas`,
        {
          title: idea.title,
          description: `${idea.hook}\n\n${idea.description}${hashtagsText}${refsText}`,
          category: idea.category
        },
        { withCredentials: true }
      );
      setSavedIds(new Set([...savedIds, index]));
      fetchSavedIdeas();
    } catch (error) {
      console.error('Failed to save idea:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API_URL}/api/content-ideas`,
        formData,
        { withCredentials: true }
      );
      setShowCreateDialog(false);
      setFormData({ title: '', description: '', category: 'Beauty' });
      fetchSavedIdeas();
    } catch (error) {
      console.error('Failed to create content idea:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]">
          <div className="text-center py-12">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#FF451A] border-r-transparent"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="content-ideas-page">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-2">Content Ideas</h1>
              <p className="text-[#6B6B6B] dark:text-zinc-400">Get AI-powered ideas trending right now or save your own</p>
            </div>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button
                  className="border border-black/[0.10] dark:border-white/10 bg-transparent text-[#0F0F0F] dark:text-white hover:bg-white/5 rounded-full px-6 py-2.5 font-medium transition-colors"
                  data-testid="create-content-idea-dialog-button"
                >
                  <Plus size={20} className="mr-2" />
                  Save Manual Idea
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Save Content Idea</DialogTitle>
          <DialogDescription className="sr-only">Dialog content — use the controls below to complete the action.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Title</label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      placeholder="10 Summer Fashion Tips"
                      className="rounded-xl px-4 py-3 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                      data-testid="content-idea-title-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                      placeholder="Describe your content idea..."
                      className="w-full rounded-xl px-4 py-3 min-h-[120px] bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                      data-testid="content-idea-description-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Category</label>
                    <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                      <SelectTrigger className="rounded-xl px-4 py-3 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="content-idea-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="submit"
                      className="flex-1 bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-6 py-2.5 font-medium hover:bg-[#E63E17] transition-colors"
                      data-testid="content-idea-submit-button"
                    >
                      Save Idea
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setShowCreateDialog(false)}
                      className="border border-black/[0.10] dark:border-white/10 bg-transparent text-[#0F0F0F] dark:text-white hover:bg-white/5 rounded-full px-6 py-2.5 font-medium"
                      data-testid="content-idea-cancel-button"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* AI Generation Section */}
          <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={20} className="text-[#FF451A]" />
              <h2 className="text-xl font-semibold text-[#0F0F0F] dark:text-white">AI Content Generator</h2>
            </div>
            <p className="text-sm text-[#6B6B6B] dark:text-zinc-400 mb-4">Select a category or type your own topic to get 8 fresh trending ideas</p>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-4">
              <form onSubmit={handleSearchSubmit} className="md:col-span-7 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B6B6B] dark:text-zinc-500" size={20} />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="e.g., 'glass skin routine' or 'winter outfits'"
                  className="rounded-xl pl-12 pr-4 py-3 w-full bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                  data-testid="content-ideas-search-input"
                />
              </form>
              <div className="md:col-span-3">
                <Select value={selectedCategory} onValueChange={handleCategorySelect}>
                  <SelectTrigger className="rounded-xl px-4 py-3 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="content-ideas-filter-select">
                    <SelectValue placeholder="Pick a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                onClick={() => generateIdeas(selectedCategory === 'all' ? '' : selectedCategory, searchQuery.trim())}
                disabled={generating || (!selectedCategory && !searchQuery.trim())}
                className="md:col-span-2 bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-xl px-6 py-3 font-medium hover:bg-[#E63E17] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="generate-ideas-button"
              >
                {generating ? 'Generating...' : 'Generate'}
              </Button>
            </div>

            {/* Quick category chips */}
            {!generating && generatedIdeas.length === 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                <span className="text-xs text-[#6B6B6B] dark:text-zinc-500 mr-2 self-center">Quick start:</span>
                {['Beauty', 'Fashion', 'Fitness', 'Food', 'Travel'].map(cat => (
                  <button
                    key={cat}
                    onClick={() => handleCategorySelect(cat)}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-[#FF451A]/10 text-[#FF451A] hover:bg-[#FF451A]/20 transition-colors"
                    data-testid={`quick-category-${cat.toLowerCase()}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Loading State */}
          {generating && (
            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-12 text-center mb-6">
              <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-[#FF451A] border-r-transparent mb-4"></div>
              <h3 className="text-lg font-semibold text-[#0F0F0F] dark:text-white mb-1">Brewing fresh ideas...</h3>
              <p className="text-sm text-[#6B6B6B] dark:text-zinc-400">Our AI is curating trending content for you</p>
            </div>
          )}

          {/* Generated Ideas */}
          {!generating && generatedIdeas.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles size={16} className="text-[#FF451A]" />
                <h3 className="text-lg font-semibold text-[#0F0F0F] dark:text-white">Fresh AI Ideas ({generatedIdeas.length})</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {generatedIdeas.map((idea, index) => (
                  <div
                    key={index}
                    className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-6 hover:-translate-y-[1px] transition-transform duration-200 flex flex-col"
                    data-testid="generated-idea-card"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#FF451A]/10 text-[#FF451A]">
                        {idea.category}
                      </span>
                      <button
                        onClick={() => saveGeneratedIdea(idea, index)}
                        disabled={savedIds.has(index)}
                        className="text-[#6B6B6B] dark:text-zinc-400 hover:text-[#FF451A] disabled:text-[#00C972] disabled:cursor-default"
                        title={savedIds.has(index) ? 'Saved' : 'Save idea'}
                        data-testid="save-generated-idea-button"
                      >
                        {savedIds.has(index) ? <BookmarkCheck size={20} /> : <Bookmark size={20} />}
                      </button>
                    </div>
                    <h3 className="text-lg font-semibold text-[#0F0F0F] dark:text-white mb-2 leading-snug">{idea.title}</h3>
                    <p className="text-sm text-[#FF451A] italic mb-3 leading-relaxed">"{idea.hook}"</p>
                    <p className="text-sm text-[#6B6B6B] dark:text-zinc-400 leading-relaxed flex-1">{idea.description}</p>

                    {idea.hashtags && idea.hashtags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-black/[0.08] dark:border-white/5">
                        {idea.hashtags.slice(0, 4).map((tag, i) => (
                          <span key={i} className="text-xs text-[#6B6B6B] dark:text-zinc-400">#{tag}</span>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-black/[0.08] dark:border-white/5 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 font-semibold mr-1">References</span>
                      {idea.instagram_url && (
                        <a
                          href={idea.instagram_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-[#FF451A]/10 text-[#FF451A] hover:bg-[#FF451A]/20 transition-colors cursor-pointer"
                          data-testid="instagram-reference-link"
                          title={idea.instagram_url}
                        >
                          <Instagram size={12} strokeWidth={2} />
                          Reels
                        </a>
                      )}
                      {idea.youtube_url && (
                        <a
                          href={idea.youtube_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-[#FF3B30]/10 text-[#FF3B30] hover:bg-[#FF3B30]/20 transition-colors cursor-pointer"
                          data-testid="youtube-reference-link"
                          title={idea.youtube_url}
                        >
                          <Youtube size={12} strokeWidth={2} />
                          Shorts
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          const lines = [];
                          if (idea.instagram_url) lines.push(`Instagram: ${idea.instagram_url}`);
                          if (idea.youtube_url) lines.push(`YouTube: ${idea.youtube_url}`);
                          const combined = lines.join('\n');
                          if (navigator.clipboard && navigator.clipboard.writeText) {
                            navigator.clipboard.writeText(combined).then(() =>
                              toast.success('Both reference links copied! Paste anywhere.', { duration: 4000 })
                            ).catch(() => {
                              const ta = document.createElement('textarea');
                              ta.value = combined;
                              document.body.appendChild(ta);
                              ta.select();
                              document.execCommand('copy');
                              document.body.removeChild(ta);
                              toast.success('Both reference links copied!', { duration: 4000 });
                            });
                          }
                        }}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-black/5 text-[#6B6B6B] dark:text-zinc-400 hover:bg-black/10 transition-colors cursor-pointer"
                        data-testid="copy-references-button"
                        title="Copy both links to clipboard"
                      >
                        Copy links
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Saved Ideas Section */}
          {savedIdeas.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Bookmark size={16} className="text-[#00C972]" />
                <h3 className="text-lg font-semibold text-[#0F0F0F] dark:text-white">Saved Ideas ({savedIdeas.length})</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {savedIdeas.map((idea) => (
                  <div
                    key={idea.id}
                    className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-6 hover:-translate-y-[1px] transition-transform duration-200"
                    data-testid="content-idea-card"
                  >
                    <div className="mb-3">
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#00C972]/10 text-[#00C972]">
                        {idea.category}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-[#0F0F0F] dark:text-white mb-2">{idea.title}</h3>
                    <p className="text-sm text-[#6B6B6B] dark:text-zinc-400 leading-relaxed whitespace-pre-line">{idea.description}</p>
                    <div className="mt-4 pt-4 border-t border-black/[0.08] dark:border-white/5 text-xs text-[#6B6B6B] dark:text-zinc-500">
                      {new Date(idea.created_at).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {savedIdeas.length === 0 && generatedIdeas.length === 0 && !generating && (
            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-12 text-center">
              <Lightbulb size={64} className="mx-auto mb-4 text-[#FF451A]" strokeWidth={1.5} />
              <h3 className="text-xl font-semibold text-[#0F0F0F] dark:text-white mb-2">Start with a category</h3>
              <p className="text-[#6B6B6B] dark:text-zinc-400 mb-6">Pick Beauty, Fashion, or any category above to get AI-generated content ideas</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
